/**
 * useActivityFeed — sticky-bottom scroll behaviour for a live, append-only
 * feed (e.g. video-call activity, chat, log streams).
 *
 * Behaviour (Discord/Slack/Terminal pattern):
 *   1. While the user is pinned to the bottom of the feed, new items
 *      auto-scroll into view.
 *   2. If the user scrolls up to read history, auto-scroll silently pauses.
 *      A "↓ X new" pill should be rendered by the caller using the returned
 *      `showJumpToLatest` + `unreadCount` values.
 *   3. When the user scrolls back within ~32 px of the bottom (or clicks the
 *      jump button), the unread count clears and auto-scroll resumes.
 *
 * The hook deliberately encapsulates all its own state. The caller passes:
 *   - `totalCount`: the running total of feed items (events + captions, etc.)
 *   - `watchKey`:   any extra value that should trigger a scroll evaluation
 *                   even when the count didn't change (e.g. an interim
 *                   caption that mutates in place).
 *
 * Returns:
 *   - `feedRef`             — attach to the scrollable container
 *   - `onScroll`            — attach as the container's onScroll handler
 *   - `jumpToLatest()`      — call from the "↓ jump" button onClick
 *   - `showJumpToLatest`    — boolean, render the pill when true
 *   - `unreadCount`         — integer, items that arrived while scrolled up
 *
 * The previous inline implementation of this logic in VideoCalls.jsx had a
 * subtle declaration-order trap: its useEffect dependency array referenced
 * state declared LATER in the component, which silently broke render. Moving
 * the logic into this hook makes the ordering contract a non-issue for
 * callers.
 */
import { useEffect, useRef, useState } from 'react';

const STICK_THRESHOLD_PX = 32;

export default function useActivityFeed({ totalCount, watchKey }) {
  const feedRef = useRef(null);
  const stickToBottomRef = useRef(true);
  // The total count at the moment the user scrolled away — used to compute
  // arrivals-since-scroll-away for the "X new" badge.
  const unreadBaselineRef = useRef(0);

  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  function onScroll() {
    const el = feedRef.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX;
    const wasStuck = stickToBottomRef.current;
    stickToBottomRef.current = atBottom;
    setShowJumpToLatest(!atBottom);
    if (!atBottom && wasStuck) {
      // User just scrolled away — snapshot the current count so we can
      // count arrivals AFTER this point (not historical entries).
      unreadBaselineRef.current = totalCount;
      setUnreadCount(0);
    } else if (atBottom) {
      // Caught back up — clear badge.
      setUnreadCount(0);
    }
  }

  function jumpToLatest() {
    const el = feedRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottomRef.current = true;
    setShowJumpToLatest(false);
    setUnreadCount(0);
  }

  // Whenever the feed grows OR the watchKey changes, either auto-scroll
  // (if pinned) or update the unread badge.
  useEffect(
    function () {
      if (stickToBottomRef.current) {
        const el = feedRef.current;
        if (!el) return undefined;
        // Defer to next frame so the new DOM node is laid out before we
        // measure scrollHeight (avoids the "scroll fires before paint" race).
        const id = window.requestAnimationFrame(function () {
          el.scrollTop = el.scrollHeight;
        });
        return function () {
          window.cancelAnimationFrame(id);
        };
      }
      const delta = totalCount - unreadBaselineRef.current;
      if (delta > 0) setUnreadCount(delta);
      return undefined;
    },
    [totalCount, watchKey],
  );

  return { feedRef, onScroll, jumpToLatest, showJumpToLatest, unreadCount };
}
